import { useSearchParams } from "react-router-dom";

import type { ChipProps } from "@braintrust/braintrust-ui-components";
import { Chip, Tooltip } from "@braintrust/braintrust-ui-components";
import { StarFilledSVGIcon as Star } from "@braintrust/braintrust-ui-components/Icons";
import type { FreelancerSuperpower } from "@js/types/freelancer";
import type { SkillForJob } from "@js/types/jobs";

type SkillChipPropsTypes<T> = {
  skill: T;
  className?: string;
};

export const JobSkillChip = ({
  className,
  skill,
}: SkillChipPropsTypes<SkillForJob>) => {
  const skillChipIcon =
    skill && skill.matched && skill.is_superpower ? (
      <Star
        style={{
          marginLeft: "-4px",
          fontSize: "15px",
          marginBottom: "2px",
        }}
      />
    ) : undefined;

  return (
    <SkillChipBase
      className={className}
      icon={skillChipIcon}
      title={skill?.name}
      label={skill?.name}
      matched={skill?.matched}
    />
  );
};

type BidSkillChipProps = SkillChipPropsTypes<SkillForJob>;

export const BidSkillChip = ({ className, skill }: BidSkillChipProps) => {
  const skillChipIcon =
    skill && skill.is_superpower ? (
      <Star
        style={{
          marginLeft: "-4px",
          fontSize: "15px",
          marginBottom: "2px",
        }}
      />
    ) : undefined;

  return (
    <SkillChipBase
      className={className}
      icon={skillChipIcon}
      label={<>{skill.name}</>}
      matched={skill.matched}
    />
  );
};

const getBidSkillChipMatchedToFilterTooltipText = ({
  skill,
}: {
  skill: SkillForJob;
}) => {
  const isTopSkillMatched = skill.is_superpower && skill.matched;
  if (isTopSkillMatched) {
    return "★ Top skill added by candidate that matches skill selected in the filter";
  }

  const isSecondarySkillMatched = skill.matched;
  if (isSecondarySkillMatched) {
    return "Skill added by candidate that matches skill selected in the filter";
  }

  return undefined;
};

const getBidSkillChipMatchedToJobTooltipText = ({
  skill,
}: {
  skill: SkillForJob;
}) => {
  const isTopSkillMatched = skill.is_superpower && skill.matched;
  if (isTopSkillMatched) {
    return "★ Top skill added by candidate that matches a skill from the job description";
  }
  const isTopSkillUnmatched = skill.is_superpower;
  if (isTopSkillUnmatched) {
    return "★ Top skill added by candidate that does not match a skill from the job description";
  }

  const isSecondarySkillMatched = skill.matched;
  if (isSecondarySkillMatched) {
    return "Skill added by candidate that matches a top skill from the job description";
  }

  return undefined;
};

export const getBidSkillChipTooltipText = ({
  skill,
  isMatchedToFilter,
}: {
  skill: SkillForJob;
  isMatchedToFilter: boolean;
}) => {
  if (isMatchedToFilter) {
    return getBidSkillChipMatchedToFilterTooltipText({ skill });
  }

  return getBidSkillChipMatchedToJobTooltipText({ skill });
};

type BidSkillChipWithTooltipProps = BidSkillChipProps & {
  isMatchedToFilter: boolean;
};

export const BidSkillChipWithTooltip = (
  props: BidSkillChipWithTooltipProps,
) => {
  const { skill, isMatchedToFilter } = props;
  const tooltipText = getBidSkillChipTooltipText({ skill, isMatchedToFilter });
  return (
    <Tooltip title={tooltipText} placement="top" maxWidth={450}>
      <div>
        <BidSkillChip {...props} />
      </div>
    </Tooltip>
  );
};

export const FreelancerSkillChip = ({
  skill,
}: SkillChipPropsTypes<FreelancerSuperpower>) => {
  const [params] = useSearchParams();

  const searchedSkills = params.get("skills")?.split(",").map(Number);
  const isSkillMatched = !!searchedSkills?.some((id) => id === skill.id);

  return (
    <SkillChipBase
      title={skill?.name}
      label={skill?.name}
      matched={isSkillMatched}
    />
  );
};

type SkillChipBaseProps = {
  matched: boolean;
} & ChipProps;

const SkillChipBase = ({
  className,
  matched,
  icon,
  ...props
}: SkillChipBaseProps) => {
  return (
    <Chip
      className={className}
      variant="rounded"
      LabelProps={{
        component: "span",
        variant: "label",
        size: "small",
        sx: { textOverflow: "ellipsis" },
      }}
      size="small"
      color={matched ? "positive-green" : "soft-grey"}
      icon={icon}
      {...props}
    />
  );
};
